import type { AnalyticsConfig, ProductEvent } from '@/types'
import { ackProductEventsPath } from '@/routes'

type AnalyticsWindow = Window & {
  rybbit?: {
    event: (name: string, props?: Record<string, string | number>) => void
    identify: (userId: string, traits?: Record<string, unknown>) => void
    clearUserId: () => void
  }
  posthog?: {
    capture: (
      name: string,
      props?: Record<string, unknown>,
      options?: { uuid?: string },
    ) => void
    identify: (id: string, traits?: Record<string, unknown>) => void
    reset: () => void
  }
}

const STORAGE_PREFIX = 'gd-product-event:'
const inflightIds = new Set<string>()

function analyticsWindow(): AnalyticsWindow | undefined {
  if (typeof window === 'undefined') return undefined
  return window as AnalyticsWindow
}

export function trackEvent(name: string, props?: Record<string, string | number>) {
  const target = analyticsWindow()
  target?.rybbit?.event(name, props)
  target?.posthog?.capture(name, props)
}

export function identifyUser(user: { id: string; email?: string | null; name?: string | null } | null) {
  const target = analyticsWindow()
  if (!target) return

  if (user?.id) {
    const traits = {
      email: user.email || undefined,
      name: user.name || undefined,
    }
    target.rybbit?.identify(user.id, traits)
    target.posthog?.identify(user.id, traits)
    return
  }

  target.rybbit?.clearUserId()
  target.posthog?.reset()
}

type BrowserContext = {
  hostname: string
  pathname: string
  search: string
  language?: string
  screenWidth?: number
  screenHeight?: number
}

export type RybbitCustomEventBody = {
  site_id: string
  type: 'custom_event'
  event_name: string
  user_id: string
  hostname: string
  pathname: string
  querystring: string
  language: string
  properties: string
  screenWidth?: number
  screenHeight?: number
}

/** Body for POST {host}/api/track. Keys match Rybbit's strict custom_event schema. */
export function buildRybbitCustomEvent(
  event: ProductEvent,
  siteId: string,
  context: BrowserContext,
): RybbitCustomEventBody {
  let properties = JSON.stringify(event.properties || {})
  if (properties.length > 2048) properties = '{}'

  const body: RybbitCustomEventBody = {
    site_id: siteId,
    type: 'custom_event',
    event_name: event.name,
    user_id: event.distinctId,
    hostname: context.hostname,
    pathname: context.pathname || '/',
    querystring: context.search || '',
    language: (context.language || 'en').slice(0, 35),
    properties,
  }
  if (context.screenWidth && context.screenWidth > 0) body.screenWidth = Math.round(context.screenWidth)
  if (context.screenHeight && context.screenHeight > 0) body.screenHeight = Math.round(context.screenHeight)
  return body
}

function rybbitTrackUrl(host: string | null | undefined): string | null {
  if (!host || !host.startsWith('https://')) return null
  return `${host.replace(/\/+$/, '')}/api/track`
}

function storageKey(id: string) {
  return `${STORAGE_PREFIX}${id}`
}

function wasDelivered(id: string): boolean {
  try {
    return sessionStorage.getItem(storageKey(id)) === '1'
  } catch {
    return false
  }
}

function markDelivered(id: string) {
  try {
    sessionStorage.setItem(storageKey(id), '1')
  } catch {
    // Private mode can reject sessionStorage. The in-memory set still covers this page.
  }
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

async function postRybbit(event: ProductEvent, config: AnalyticsConfig): Promise<boolean> {
  const siteId = config.rybbitSiteId?.trim()
  const url = rybbitTrackUrl(config.rybbitHost)
  if (!siteId || !url) return false

  const body = buildRybbitCustomEvent(event, siteId, {
    hostname: window.location.hostname,
    pathname: window.location.pathname,
    search: window.location.search,
    language: navigator.language,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
  })

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    mode: 'cors',
    credentials: 'omit',
    keepalive: true,
  })
  if (!response.ok) return false

  const data = (await response.json().catch(() => null)) as { success?: boolean; message?: string } | null
  if (data?.success === false) return false
  if (typeof data?.message === 'string' && data.message.includes('not tracked')) return false
  return true
}

async function deliverOne(event: ProductEvent, config: AnalyticsConfig): Promise<boolean> {
  const rybbitConfigured = Boolean(config.rybbitSiteId?.trim() && rybbitTrackUrl(config.rybbitHost))
  // Nothing for the browser to send. PostHog already captured this on the server.
  if (!rybbitConfigured) return true

  try {
    return await postRybbit(event, config)
  } catch {
    return false
  }
}

async function ackEvents(ids: string[]) {
  if (ids.length === 0) return

  await fetch(ackProductEventsPath(), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken(),
    },
    credentials: 'same-origin',
    body: JSON.stringify({ ids }),
  })
}

/**
 * Sends queued funnel events to Rybbit from the browser, on the same /api/track
 * path as pageviews. PostHog is not called here: Analytics.track already captured
 * these events on the server, and a second capture would double-count.
 */
export async function flushProductEvents(
  events: ProductEvent[] | null | undefined,
  config: AnalyticsConfig | null | undefined,
): Promise<void> {
  if (!events?.length || typeof window === 'undefined') return

  const analytics: AnalyticsConfig = config ?? { rybbitHost: null, rybbitSiteId: null }
  const ackIds: string[] = []

  for (const event of events) {
    if (!event?.id || !event.name || !event.distinctId) continue
    if (wasDelivered(event.id)) {
      ackIds.push(event.id)
      continue
    }
    if (inflightIds.has(event.id)) continue

    inflightIds.add(event.id)
    const ok = await deliverOne(event, analytics)
    inflightIds.delete(event.id)
    if (!ok) continue

    markDelivered(event.id)
    ackIds.push(event.id)
  }

  if (ackIds.length > 0) await ackEvents(ackIds)
}
